/* global fetch */
import {axios, jwt, lockr, Sentry, uiConstants} from '@api'
import {nav, globalStores as stores, createGlobalStores, StoresInstance, globalStores} from '@stores'
import {action, observable, computed, when} from 'mobx'
import Cookies from 'universal-cookie'

const cookies = new Cookies()

export interface IUserInfo {
  id: string
  username: string
  firstName: string
  lastName: string
  sessionUrl: string
  client_id: string
  jti: string
  exp: number
  iat: number
  roles?: Array<{ id?, name? }>
}

export interface ILoginResult {
  access_token: string
  refresh_token?: string
  token_type: string
  user: {
    id: string
    username: string
  }

  securityTokenURL: string
}

const parseToken = (accessToken: string) => {
  let decodedToken = {}
  try {
    decodedToken = JSON.parse(atob(accessToken.split('.')[1]))
  } catch (e) {
    console.log('Error in parsing token!')
  }
  return decodedToken
}

/**
 * Removes all information related to user (access tokens, refresh token)
 */
const invalidateUser = async () => {
  console.debug(`Invalidate user`)
  await authStore.logout()
}

export interface IAuthSerialized {
  securityTokenURL: string
  accessToken: string
  refreshToken: string
  user: IUserInfo
}

export class AuthStore {
  @observable isLoggingIn = false
  @observable isLoadingUser = false

  @computed get securityTokenURL() { return this.authResult?.securityTokenURL }
  @computed get authStorageKey() {
    return `auth`
  }

  @observable.ref authResult : IAuthSerialized

  private set authFromLocalStorage(v) {
    lockr.set(this.authStorageKey, v)
  }

  get accessToken() {
    return this.authResult?.accessToken
  }

  /**
   * Checks if user is authenticated
   */
  @action checkIsAuthenticated = () => {
    const authFromLocalStorage = lockr.get(this.authStorageKey) as IAuthSerialized
    const isThoughtToBeAuthenticated = !_.isEmpty(authFromLocalStorage)
    if (isThoughtToBeAuthenticated != this.userIsAuthenticated) {
      this.authResult = authFromLocalStorage
      this.userIsAuthenticated = isThoughtToBeAuthenticated
      const {user} = this

      if (user) {
        const userName = user['username']
        Sentry.addBreadcrumb({message: `Authenticated user ${userName}`})
        Sentry.configureScope(function (scope) {
          scope.setUser({'username': userName})
        })
        setTimeout(async () => {
          const {users} = stores
          this.isLoadingUser = true
          await users._loadUser_c8(user.id)
          this.isLoadingUser = false
        }, 100)
      }
    }
    const bookmarkQueryParam = {...nav.queryParams}
    if (!isThoughtToBeAuthenticated && !this.logoutClicked && nav.location.pathname?.includes('vip') && !_.isEmpty(bookmarkQueryParam)) {
      this.logoutClicked = true
      setTimeout(() => {
        nav.updateQueryParams(bookmarkQueryParam, 'replace')
      }, 200)
    }
    return isThoughtToBeAuthenticated
  }

  @computed get user(): IUserInfo {
    return this.authResult?.user
  }

  @observable logoutClicked: Boolean = false

  @action logout = async (initiatedByUserAction = false, navigateToLogoutPage = true) => {
    // If you clear local storage you lose everything including layout!
    // if (initiatedByUserAction) {
    //   stores.catalog.settingsStore.clearLocalStorage()
    // }

    const {authFromLocalStorage} = this

    this.logoutClicked = true
    this.userIsAuthenticated = false
    this.authResult = null

    nav.removeLockrHistory()
    lockr.rm(this.authStorageKey)
    cookies.remove('SecurityTokenURL')
    //lockr.flush() This kills any sort of user level settings

    DEBUG && console.log(`Logout user`)

    Sentry.addBreadcrumb({category: 'auth', level: Sentry.Severity.Info, message: `Logged out user ${authFromLocalStorage?.user ? authFromLocalStorage?.user['username'] : 'unknown'}`})
    Sentry.configureScope(function (scope) {
      scope.setUser({'username': undefined})
    })


    const {apollo: {client}, site, vlp, c8, filter} = stores
    try {
      await client.cache.reset()
      await client.store.reset()

      StoresInstance.allStores.forEach(s => !s.isGlobalStore && s.dispose())
    } catch (err) {
      console.error(err)
    }

    if (navigateToLogoutPage && !window.location.pathname.startsWith('/logout') && !window.location.pathname.startsWith('/login')) {
      console.log(`Navigating from ${nav.location.pathname} to /logout`)
      const {deploymentQueryParam, stores: {deployments: {selectedDeploymentId}}} = nav
      nav.reactRouter.history.replace({ search: DEBUG && selectedDeploymentId ? `deployment=${selectedDeploymentId}` : null, pathname: '/logout'})
      //window.location.pathname = `/logout`${deploymentQueryParam ? `?${deploymentQueryParam}` : ''}`
      this.logoutClicked = false
    }
  }
  @observable userIsAuthenticated = false

  @computed get authStoreHeaders() {
    const {userIsAuthenticated, accessToken} = this
    return userIsAuthenticated ? {authorization: `Bearer ${accessToken}`} : {}
  }
  lastTokenVerifiedTimeEs: Date

  @computed get expiration() {
    const {accessToken} = this
    if (accessToken) {
      const d = jwt.decode(accessToken)

      // Server did:  Math.floor(today.setHours(today.getHours() + 24) / 1000)
      const expiration = new Date(d['exp'] * 1000)

      return expiration
    }

    return null
  }

  verifyTokenNotExpired = async () => {
    const {accessToken, userIsAuthenticated} = this

    if (userIsAuthenticated) {
      let isExpired = true
      if (accessToken) {
        const {expiration} = this
        isExpired = (expiration.valueOf() - Date.now().valueOf()) <= 0
      }

      // if (!isExpired) {
      //   // Verify ES token is still valid
      //   try {
      //     if (!this.lastTokenVerifiedTimeEs) {
      //       await stores.es.ping()
      //       this.lastTokenVerifiedTimeEs = new Date()
      //     }
      //   } catch (err) {
      //     console.debug(`Ping of ES resulted in expiration`)
      //     isExpired = true
      //   }
      // }

      if (isExpired) {
        console.warn(`Token has expired, logging the user out - TODO - we could renew the token`, {accessToken, expiration: this.expiration})
        await this.logout()
      } else {
      }
    }
  }

  @action onLoginSuccess = async (result: ILoginResult) => {
    // Save token in local storage
    authStore.isLoggingIn = false
    this.authResult = this.authFromLocalStorage = {
      ...(this.authFromLocalStorage ?? {}),
      accessToken: result.access_token,
      refreshToken: result.refresh_token ?? null,
      user: parseToken(result.access_token) as IUserInfo,
      securityTokenURL: result.securityTokenURL
    }
    cookies.set('SecurityTokenURL', result.securityTokenURL, {path: '/'})

    this.userIsAuthenticated = true
    Sentry.addBreadcrumb({category: 'auth', level: Sentry.Severity.Info, message: `Logged in user ${result.user ? result.user['username'] : '??? (user property is null)'}`})
    Sentry.configureScope(function (scope) {
      scope.setUser({'username': result.user.username})
    })

    const {users} = createGlobalStores()

    users.getById(result.user.id)

    when(() => this.user != null, () => {
      // Redirect user to appropriate URL
      const {nav} = stores,
        {pathname, search}= nav.getHistoryStateFromLockr()
      let {from} = nav.location.state || {from: {pathname: '/'}}
      if (pathname && pathname.startsWith('/logout')) {

      }
      else {
        nav.history.push({
          pathname: pathname ?? from.pathname,
          search: search ?? from.search ?? undefined
        })
      }

      nav.removeLockrHistory()
    })
  }

  @computed
  get userLockr() {
    return lockr.get('user')
  }
}

const authStore = new AuthStore()

export {invalidateUser, authStore}


/*
Removed from NavStore - was for qr code

//
if (this.queryParams['x-access-token']) {
  if (!authStore.userIsAuthenticated) {
    lockr.set('access_token', this.queryParams['x-access-token'])
    const date = new Date()
    date.setHours(date.getHours() + 24)
    const currentTime = Math.floor(date.getTime() / 1000)
    lockr.set('access_token_expiry_date', currentTime)
  }

  setTimeout(() => this.updateQueryParams({..._.omit(this.queryParams, 'x-access-token')}, 'replace'), 10)
}
 */


// WEBPACK FOOTER //
// ./src/utils/authUtils.ts